import { authService, PermissionScope } from '@/services/AuthService';
import PermissionGuard from './PermissionGuard';
import { RESOURCES } from '@/constants/resources';
import { Device } from '@/models/device';
import { CustomField, SubCustomField } from '@/models/customField';

export const CONFIG_STATUS_MAP = {
    Pending: { label: 'Update Pending', className: 'unknown' },
    Failed: { label: 'Update Failed', className: 'offline' },
    InSync: { label: 'No Update Pending', className: 'online' },
    NotAssigned: { label: 'Not Assigned', className: 'unknown' }
} as const;

export const FIRMWARE_STATUS_MAP = {
  Pending: { label: 'Update Pending', className: 'unknown' },
  Failed: { label: 'Update Failed', className: 'offline' },
  Healthy: { label: 'No Update Pending', className: 'online' },
  NotAssigned: { label: 'Not Assigned', className: 'unknown' }
} as const;

// Helper type to ensure type safety with the API strings
type ConfigurationStatus = keyof typeof CONFIG_STATUS_MAP;
type FirmwareStatus = keyof typeof FIRMWARE_STATUS_MAP;

interface DeviceTableRowProps {
  device: Device
  customFields: CustomField[],
  deviceSubCustomFields: SubCustomField[],
  canEditDevices: boolean
  isSelected: boolean
  onSelectDevice: (device: Device) => void
  onEdit: (device: Device) => void
  onLog: (device: Device) => void
  onDelete: (device: Device) => void
}

export default function DeviceTableRow({
  device,
  customFields,
  deviceSubCustomFields,
  canEditDevices,
  isSelected,
  onSelectDevice,
  onEdit,
  onLog,
  onDelete
}: DeviceTableRowProps) {
  const hasCustomFieldsPermission = authService.hasPermission(RESOURCES.CUSTOM_FIELDS, PermissionScope.READ);
    
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const configurationStatusKey = device.configurationStatus as ConfigurationStatus;
  const configurationStatus = CONFIG_STATUS_MAP[configurationStatusKey];

  const firmwareStatusKey = device.firmwareStatus as FirmwareStatus;
  const firmwareStatus = FIRMWARE_STATUS_MAP[firmwareStatusKey];
  
  return (
    <tr>
      {canEditDevices && (
        <td>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelectDevice(device)}
          />
        </td>
      )}
      <td>{device.serialNumber}</td>
      <td>{device.imei || '-'}</td>
      <td>{device.type || '-'}</td>
      <td>{device.phoneNumber || '-'}</td>
      <td>{device.simSerialNumber || '-'}</td>
      <td>{device.simProviderName || '-'}</td>
      {customFields.map((field) => {
        if (deviceSubCustomFields == null) {
          return (
            <td>-</td>
          );
        }

        const subItem = deviceSubCustomFields.find(scf => scf.parentCustomFieldId === field.id);
        if (subItem != null) {
          return (
            <td>{subItem.name}</td>
          );
        }

        return (
          <td>-</td>
        );
        
      })}
      <td>{device.firmware || '-'}</td>
      <td>{device.configurationName || '-'}</td>
      <td>{formatDate(device.dateAddedUtc)}</td>
      <td>
          <span className={`health-status ${configurationStatus.className}`}>
            {configurationStatus.label}
          </span>
      </td>
      <td>
        <span className={`health-status ${firmwareStatus.className}`}>
            {firmwareStatus.label}
        </span>
      </td>

      {canEditDevices && (
        <td className="actions">
          <PermissionGuard resource={RESOURCES.DEVICES} scope={PermissionScope.UPDATE}>
            <button
              className="action-btn edit-btn"
              onClick={() => onEdit(device)}
              title="Edit device"
            >
              Edit
            </button>
          </PermissionGuard>
          
          <PermissionGuard resource={RESOURCES.DEVICES} scope={PermissionScope.READ}>
            <button
              className="action-btn log-btn"
              onClick={() => onLog(device)}
              title="View device audit trail"
            >
              Audit
            </button>
          </PermissionGuard>
         
          <PermissionGuard resource={RESOURCES.DEVICES} scope={PermissionScope.DELETE}>
            <button
              className="action-btn delete-btn"
              onClick={() => onDelete(device)}
              title="Delete device"
            >
              Delete
            </button>
          </PermissionGuard>
        
        </td>
      )}
    </tr>
  )
}